/**
 * STFFLG-4185: the ONE place a Settings row is decided.
 *
 * The Settings nav, /settings, the user menu's Settings item, the route guard for every Settings
 * route (RouteRenderer in AppRoutes) and each page's own controls all ask resolveSettingsRow, so
 * none of them can answer differently from another. Before it, the nav and the route guard read
 * the tree one way (hasModulePermission, with a Platform pass on an absent node) and the in-page
 * controls another (usePermission, an absent child = denied), and the two disagreed exactly where
 * a row was missing.
 *
 * A row present in the tree, an explicit all-false deny included, decides by its own flags. A row
 * the tree lacks, or a role with no tree at all, answers with the page's absentDefault
 * (settingsPagesData.ts says why each default is what it is).
 */
import {useMemo} from 'react'
import {resolvePermission, toPermissionCheck} from '~/src/hooks/resolvePermission'
import type {PermissionNode} from '~/src/hooks/resolvePermission'
import {useAppSelector} from '~/src/store/store-hooks'
import type {BackendRolePermissions} from '~/src/utils/permissions'
import {SETTINGS_PAGES_DATA} from './settingsPagesData'
import type {SettingsOperation, SettingsPageData, SettingsPermissionKey} from './settingsPagesData'

export interface SettingsRowPermission {
  canView: boolean
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  /** The row was in the tree; false = the answer is the page's absentDefault. */
  matched: boolean
}

const SETTINGS_MODULE_KEY = 'settings'

/** The row under Settings, at any depth (eSign Documents sits one level down, under eSign). */
const findRow = (nodes: PermissionNode[] | undefined, key: string): PermissionNode | undefined => {
  for (const node of nodes ?? []) {
    if (node.key === key) return node
    const nested = findRow(node.submodules, key)
    if (nested) return nested
  }
  return undefined
}

const offering = (ops: readonly SettingsOperation[], allowed: boolean) => ({
  canView: allowed && ops.includes('View'),
  canCreate: allowed && ops.includes('Create'),
  canEdit: allowed && ops.includes('Edit'),
  canDelete: allowed && ops.includes('Delete'),
})

const absentAnswer = (
  permissions: BackendRolePermissions | null | undefined,
  page: SettingsPageData,
): boolean => {
  if (page.absentDefault === 'allow') return true
  if (page.absentDefault === 'rolesPermissionsView') {
    // The stand-in exactly as these pages read it before they had rows.
    return resolvePermission(permissions, 'global', 'roles_permissions').canView
  }
  return false
}

const pageForKey = (key: SettingsPermissionKey): SettingsPageData => {
  const page = SETTINGS_PAGES_DATA.find((entry) => entry.permissionKey === key)
  // Unreachable while SettingsPermissionKey and the data agree; settingsPages.test.ts pins that.
  if (!page) throw new Error(`No Settings page for row '${key}'`)
  return page
}

/**
 * What the role may do on a Settings page's row. Pure: the route guard calls it outside React.
 *
 * Every row-absent answer comes from `absentDefault`, whether the tree lacks the row, lacks the whole
 * Settings module, does not exist (a legacy accessList-only role), or no role is in the store at all
 * (sign-in stored none). Before these rows existed, Work Authorization and Help / FAQ were open to
 * every one of those users, so none of them may lose the two pages now; every surface reads it here,
 * so the Help icon, the route and the menu agree.
 */
export const resolveSettingsRow = (
  permissions: BackendRolePermissions | null | undefined,
  pageOrKey: SettingsPageData | SettingsPermissionKey,
): SettingsRowPermission => {
  const page = typeof pageOrKey === 'string' ? pageForKey(pageOrKey) : pageOrKey
  const tree: PermissionNode[] | undefined = permissions?.modulePermissions
  const settings = Array.isArray(tree)
    ? tree.find((module) => module.key === SETTINGS_MODULE_KEY)
    : undefined
  const row = findRow(settings?.submodules, page.permissionKey)
  if (row) {
    const {canView, canCreate, canEdit, canDelete} = toPermissionCheck(row)
    return {canView, canCreate, canEdit, canDelete, matched: true}
  }
  return {
    ...offering(page.availableOperations, absentAnswer(permissions, page)),
    matched: false,
  }
}

/** The Settings page a routeConfig route serves, or undefined for any other route. */
export const findSettingsPageByRouteId = (routeId: string): SettingsPageData | undefined =>
  SETTINGS_PAGES_DATA.find((page) => page.routeId === routeId)

/**
 * The Settings page whose row a route is gated on: the page the route serves, or, for a second
 * route onto the same screen (Global > Integrations > Zoom renders Zoom Setup's page), the page
 * whose row the route names. Undefined for any route that is not gated on a Settings page's row
 * (eSign Signing gates /my-signatures but is no Settings page).
 */
export const findSettingsPageForRoute = (route: {
  id: string
  moduleKey?: string
  submoduleKey?: string
}): SettingsPageData | undefined =>
  findSettingsPageByRouteId(route.id) ??
  (route.moduleKey === SETTINGS_MODULE_KEY
    ? SETTINGS_PAGES_DATA.find((page) => page.permissionKey === route.submoduleKey)
    : undefined)

/** The signed-in role's answer for one Settings row (see resolveSettingsRow). */
export const useSettingsRowPermission = (key: SettingsPermissionKey): SettingsRowPermission => {
  const permissions = useAppSelector((state) => state.root.backendRolePermissions)
  return useMemo(() => resolveSettingsRow(permissions, key), [permissions, key])
}
